/**
 * Service: RBAC
 * Serviço para gerenciamento de papéis e permissões no frontend
 */

import api from './api';
import {
  IPermission,
  IRole,
  IUserRole,
  IRoleAudit,
  ICreatePermissionData,
  ICreateRoleData,
  IUpdateRoleData,
  IGrantRoleData,
  IRevokeRoleData,
  IPermissionFilters,
  IRoleFilters,
  IUserRoleFilters,
  IAuditFilters,
  IPermissionCheckResult,
  IUserPermissionsResult,
  IRBACStats,
  IRBACApiResponse,
  IPaginatedResponse,
  IRBACRolesResponse,
  IRBACPermissionsResponse,
  PermissionString,
  RoleString
} from '../types/rbac';

class RBACService {
  private baseUrl = '/api/rbac';

  // ================================
  // PERMISSÕES
  // ================================

  /**
   * Listar permissões
   */
  async getPermissions(filters?: IPermissionFilters): Promise<IRBACPermissionsResponse> {
    const params = new URLSearchParams();
    
    if (filters?.recurso) params.append('recurso', filters.recurso);
    if (filters?.acao) params.append('acao', filters.acao);
    if (filters?.nome) params.append('nome', filters.nome);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await api.get<IRBACApiResponse<IRBACPermissionsResponse>>(
      `${this.baseUrl}/permissions?${params.toString()}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao buscar permissões');
    }

    return response.data.data!;
  }

  /**
   * Obter permissão por ID
   */
  async getPermissionById(id: string): Promise<IPermission> {
    const response = await api.get<IRBACApiResponse<IPermission>>(
      `${this.baseUrl}/permissions/${id}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao buscar permissão');
    }

    return response.data.data!;
  }

  /**
   * Criar nova permissão
   */
  async createPermission(data: ICreatePermissionData): Promise<IPermission> {
    const response = await api.post<IRBACApiResponse<IPermission>>(
      `${this.baseUrl}/permissions`,
      data
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao criar permissão');
    }

    return response.data.data!;
  }

  /**
   * Atualizar permissão
   */
  async updatePermission(permissionId: string, data: Partial<ICreatePermissionData>): Promise<IPermission> {
    const response = await api.put<IRBACApiResponse<IPermission>>(
      `${this.baseUrl}/permissions/${permissionId}`,
      data
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao atualizar permissão');
    }

    return response.data.data!;
  }

  /**
   * Excluir permissão
   */
  async deletePermission(permissionId: string): Promise<void> {
    const response = await api.delete<IRBACApiResponse<void>>(
      `${this.baseUrl}/permissions/${permissionId}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao excluir permissão');
    }
  }

  // ================================
  // PAPÉIS
  // ================================

  /**
   * Listar papéis
   */
  async getRoles(filters?: IRoleFilters): Promise<IRBACRolesResponse> {
    const params = new URLSearchParams();
    
    if (filters?.escopo) params.append('escopo', filters.escopo);
    if (filters?.nome) params.append('nome', filters.nome);
    if (filters?.incluirPermissoes) params.append('incluirPermissoes', 'true');
    if (filters?.ehPapelSistema !== undefined) {
      params.append('ehPapelSistema', filters.ehPapelSistema.toString());
    }
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await api.get<IRBACApiResponse<IRBACRolesResponse>>(
      `${this.baseUrl}/roles?${params.toString()}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao buscar papéis');
    }

    return response.data.data!;
  }

  /**
   * Obter papel por ID
   */
  async getRoleById(id: string, incluirPermissoes = false): Promise<IRole> {
    const params = incluirPermissoes ? '?incluirPermissoes=true' : '';
    
    const response = await api.get<IRBACApiResponse<IRole>>(
      `${this.baseUrl}/roles/${id}${params}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao buscar papel');
    }

    return response.data.data!;
  }

  /**
   * Criar novo papel
   */
  async createRole(data: ICreateRoleData): Promise<IRole> {
    const response = await api.post<IRBACApiResponse<IRole>>(
      `${this.baseUrl}/roles`,
      data
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao criar papel');
    }

    return response.data.data!;
  }

  /**
   * Atualizar papel
   */
  async updateRole(id: string, data: IUpdateRoleData): Promise<IRole> {
    const response = await api.put<IRBACApiResponse<IRole>>(
      `${this.baseUrl}/roles/${id}`,
      data
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao atualizar papel');
    }

    return response.data.data!;
  }

  /**
   * Deletar papel
   */
  async deleteRole(id: string): Promise<void> {
    const response = await api.delete<IRBACApiResponse<void>>(
      `${this.baseUrl}/roles/${id}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao deletar papel');
    }
  }

  // ================================
  // PAPÉIS DE USUÁRIO
  // ================================

  /**
   * Listar todas as atribuições de papéis do sistema
   */
  async getAllUserRoles(filters?: IUserRoleFilters): Promise<IUserRole[]> {
    const params = new URLSearchParams();
    
    if (filters?.incluirPapel) params.append('incluirPapel', 'true');
    if (filters?.incluirProjeto) params.append('incluirProjeto', 'true');
    if (filters?.incluirUsuario) params.append('incluirUsuario', 'true');
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await api.get<IRBACApiResponse<IUserRole[]>>(
      `${this.baseUrl}/user-roles?${params.toString()}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao buscar atribuições de papéis');
    }

    return response.data.data!;
  }

  /**
   * Listar papéis de um usuário
   */
  async getUserRoles(
    userId: string, 
    filters?: Omit<IUserRoleFilters, 'usuarioId'>
  ): Promise<IUserRole[]> {
    const params = new URLSearchParams();
    
    if (filters?.incluirPapel) params.append('incluirPapel', 'true');
    if (filters?.incluirProjeto) params.append('incluirProjeto', 'true');

    const response = await api.get<IRBACApiResponse<IUserRole[]>>(
      `${this.baseUrl}/users/${userId}/roles?${params.toString()}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao buscar papéis do usuário');
    }

    return response.data.data!;
  }

  /**
   * Obter permissões efetivas de um usuário
   */
  async getUserPermissions(userId: string, projetoId?: string): Promise<IUserPermissionsResult> {
    const params = projetoId ? `?projetoId=${projetoId}` : '';
    
    const response = await api.get<IRBACApiResponse<IUserPermissionsResult>>(
      `${this.baseUrl}/users/${userId}/permissions${params}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao buscar permissões do usuário');
    }

    return response.data.data!;
  }

  /**
   * Conceder papel a usuário
   */
  async grantRole(data: IGrantRoleData): Promise<IUserRole> {
    const response = await api.post<IRBACApiResponse<IUserRole>>(
      `${this.baseUrl}/roles/grant`,
      data
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao conceder papel');
    }

    return response.data.data!;
  }

  /**
   * Revogar papel de usuário
   */
  async revokeRole(data: IRevokeRoleData): Promise<void> {
    const response = await api.post<IRBACApiResponse<void>>(
      `${this.baseUrl}/roles/revoke`,
      data
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao revogar papel');
    }
  }

  /**
   * Renovar papel (estender expiração)
   */
  async renewRole(
    userRoleId: string, 
    novaDataExpiracao?: Date, 
    motivo?: string
  ): Promise<void> {
    const response = await api.put<IRBACApiResponse<void>>(
      `${this.baseUrl}/user-roles/${userRoleId}/renew`,
      {
        novaDataExpiracao,
        motivo
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao renovar papel');
    }
  }

  // ================================
  // VERIFICAÇÕES
  // ================================

  /**
   * Verificar se usuário tem permissão específica
   */
  async checkPermission(
    userId: string, 
    permission: PermissionString, 
    projectId?: string
  ): Promise<IPermissionCheckResult> {
    const params = projectId ? `?projetoId=${projectId}` : '';
    
    const response = await api.get<IRBACApiResponse<IPermissionCheckResult>>(
      `${this.baseUrl}/check/permission/${userId}/${encodeURIComponent(permission)}${params}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao verificar permissão');
    }

    return response.data.data!;
  }

  /**
   * Verificar se usuário tem papel específico
   */
  async checkRole(
    userId: string, 
    role: RoleString, 
    projectId?: string
  ): Promise<{ hasRole: boolean; userId: string; role: string; projectId?: string; checkedAt: Date }> {
    const params = projectId ? `?projetoId=${projectId}` : '';
    
    const response = await api.get<IRBACApiResponse<any>>(
      `${this.baseUrl}/check/role/${userId}/${encodeURIComponent(role)}${params}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao verificar papel');
    }

    return response.data.data!;
  }

  // ================================
  // AUDITORIA
  // ================================

  /**
   * Listar registros de auditoria
   */
  async getAuditLogs(filters?: IAuditFilters): Promise<IPaginatedResponse<IRoleAudit>> {
    const params = new URLSearchParams();
    
    if (filters?.usuarioId) params.append('usuarioId', filters.usuarioId);
    if (filters?.papelId) params.append('papelId', filters.papelId);
    if (filters?.projetoId) params.append('projetoId', filters.projetoId);
    if (filters?.acao) params.append('acao', filters.acao);
    if (filters?.executadoPor) params.append('executadoPor', filters.executadoPor);
    if (filters?.dataInicio) params.append('dataInicio', filters.dataInicio.toISOString());
    if (filters?.dataFim) params.append('dataFim', filters.dataFim.toISOString());
    if (filters?.incluirPapel) params.append('incluirPapel', 'true');
    if (filters?.incluirUsuario) params.append('incluirUsuario', 'true');
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await api.get<IRBACApiResponse<IPaginatedResponse<IRoleAudit>>>(
      `${this.baseUrl}/audit?${params.toString()}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao buscar logs de auditoria');
    }

    return response.data.data!;
  }

  /**
   * Obter atividade recente
   */
  async getRecentActivity(horas = 24, limit = 50): Promise<IRoleAudit[]> {
    const params = new URLSearchParams();
    params.append('horas', horas.toString());
    params.append('limit', limit.toString());

    const response = await api.get<IRBACApiResponse<IRoleAudit[]>>(
      `${this.baseUrl}/audit/recent?${params.toString()}`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao buscar atividade recente');
    }

    return response.data.data!;
  }

  // ================================
  // ADMINISTRAÇÃO
  // ================================

  /**
   * Obter estatísticas do sistema RBAC
   */
  async getStats(): Promise<IRBACStats> {
    const response = await api.get<IRBACApiResponse<IRBACStats>>(
      `${this.baseUrl}/stats`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao obter estatísticas');
    }

    return response.data.data!;
  }

  /**
   * Processar papéis expirados
   */
  async processExpiredRoles(): Promise<{ processedCount: number; message: string }> {
    const response = await api.post<IRBACApiResponse<{ processedCount: number; message: string }>>(
      `${this.baseUrl}/maintenance/expired-roles`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao processar papéis expirados');
    }

    return response.data.data!;
  }

  /**
   * Limpar cache RBAC
   */
  async clearCache(): Promise<void> {
    const response = await api.post<IRBACApiResponse<void>>(
      `${this.baseUrl}/maintenance/clear-cache`
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Erro ao limpar cache');
    }
  }

  // ================================
  // UTILITÁRIOS
  // ================================

  /**
   * Formatar permissão para exibição
   */
  formatPermission(permission: IPermission): string {
    return `${permission.recurso}.${permission.acao}`;
  }

  /**
   * Obter descrição legível da ação de auditoria
   */
  getAuditActionDescription(action: string): string {
    const actionMap: Record<string, string> = {
      'concedido': 'Papel concedido',
      'revogado': 'Papel revogado',
      'expirado': 'Papel expirado',
      'renovado': 'Papel renovado',
      'suspenso': 'Papel suspenso',
      'reativado': 'Papel reativado'
    };
    
    return actionMap[action] || action;
  }

  /**
   * Verificar se papel está expirando em breve
   */
  isRoleExpiringSoon(userRole: IUserRole, daysThreshold = 7): boolean {
    if (!userRole.expiraEm || !userRole.ativo) return false;
    
    const now = new Date();
    const expiry = new Date(userRole.expiraEm);
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    return diffDays <= daysThreshold && diffDays > 0;
  }

  /**
   * Verificar se papel está expirado
   */
  isRoleExpired(userRole: IUserRole): boolean {
    if (!userRole.expiraEm) return false;
    
    const now = new Date();
    const expiry = new Date(userRole.expiraEm);
    
    return expiry < now;
  }

  /**
   * Obter contexto do papel (global ou projeto)
   */
  getRoleContext(userRole: IUserRole): { type: 'global' | 'project'; projectName?: string } {
    if (!userRole.projetoId) {
      return { type: 'global' };
    }
    
    return {
      type: 'project',
      projectName: userRole.projeto?.nome
    };
  }
}

export const rbacService = new RBACService();
export default rbacService;